class FreeGiftManager {
  constructor() {
    this.config = window.freeGiftConfig;
    if (!this.config?.enabled || !this.config.tiers?.length) return;

    this.tiers = this.config.tiers.filter((tier) => tier.configured && tier.variantId);
    if (!this.tiers.length) return;

    this.syncing = false;
    this.previousCart = null;
    this.previousFingerprint = null;

    this.init();
  }

  init() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'free-gift') return;
      this.sync();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.sync());
    } else {
      this.sync();
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.dataset.freeGiftOpenBound) {
      drawer.dataset.freeGiftOpenBound = 'true';
      const originalOpen = drawer.open.bind(drawer);
      drawer.open = (...args) => {
        originalOpen(...args);
        this.sync();
      };
    }
  }

  getDismissedAttribute(tierId) {
    return `_free_gift_dismissed_${tierId}`;
  }

  isGiftLine(item) {
    const value = item.properties?.[this.config.giftProperty];
    return value === 'true' || value === '1' || value === '2';
  }

  getGiftTierValue(item) {
    const value = item.properties?.[this.config.giftProperty];
    if (value === 'true' || value === '1') return 1;
    if (value === '2') return 2;
    return null;
  }

  getGiftLine(cart, tierId) {
    return cart.items.find((item) => {
      const tierValue = this.getGiftTierValue(item);
      return tierValue === tierId;
    });
  }

  getEligibleSubtotal(cart) {
    let subtotal = cart.items_subtotal_price;
    for (const item of cart.items) {
      if (this.isGiftLine(item)) {
        subtotal -= item.final_line_price;
      }
    }
    return subtotal;
  }

  getNonGiftFingerprint(cart) {
    if (!cart?.items?.length) return '';
    return cart.items
      .filter((item) => !this.isGiftLine(item))
      .map((item) => `${item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  isDismissed(cart, tierId) {
    const attribute = this.getDismissedAttribute(tierId);
    if (cart.attributes?.[attribute] === 'true') return true;
    if (tierId === 1 && cart.attributes?.[this.config.legacyDismissedAttribute] === 'true') {
      return true;
    }
    return false;
  }

  async setDismissed(tierId, dismissed) {
    const attributes = {
      [this.getDismissedAttribute(tierId)]: dismissed ? 'true' : '',
    };
    if (tierId === 1) {
      attributes[this.config.legacyDismissedAttribute] = dismissed ? 'true' : '';
    }

    await fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes }),
    });
  }

  async fetchCart() {
    const response = await fetch(`${window.routes.cart_url}.js`);
    return response.json();
  }

  async refreshCartUI() {
    if (document.querySelector('cart-drawer')) {
      await this.refreshDrawerSections();
    }

    if (document.getElementById('main-cart-footer')) {
      await this.refreshCartPageSections();
    }
  }

  async refreshDrawerSections() {
    await cartDomSync.replaceDrawerInner((signal) =>
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`, { signal }).then((response) => response.text())
    );
  }

  async refreshCartPageSections() {
    const cartItems = document.querySelector('cart-items');
    await cartDomSync.replaceCartPage(
      (signal) =>
        Promise.all([
          fetch(`${window.routes.cart_url}?section_id=main-cart-items`, { signal }),
          fetch(`${window.routes.cart_url}?section_id=main-cart-footer`, { signal }),
        ]).then(([itemsResponse, footerResponse]) =>
          Promise.all([itemsResponse.text(), footerResponse.text()])
        ),
      cartItems
    );
  }

  async refreshCartIcon() {
    const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const target = document.getElementById('cart-icon-bubble');
    const source = html.querySelector('.shopify-section');
    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  async addGift(tier) {
    const response = await fetch(window.routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [
          {
            id: Number(tier.variantId),
            quantity: 1,
            properties: {
              [this.config.giftProperty]: String(tier.id),
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to add free gift');
    }

    return response.json();
  }

  async removeGift(cart, giftLine) {
    const line = cart.items.indexOf(giftLine) + 1;
    const response = await fetch(window.routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        line,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to remove free gift');
    }

    return response.json();
  }

  isFullCart(cartData) {
    return Boolean(cartData?.token && Array.isArray(cartData?.items));
  }

  async resolveCartResponse(cartData) {
    if (this.isFullCart(cartData)) {
      return cartData;
    }
    return this.fetchCart();
  }

  async replaceGift(cart, removeLine, addTier) {
    await this.removeGift(cart, removeLine);
    const addResponse = await this.addGift(addTier);
    return this.resolveCartResponse(addResponse);
  }

  isTierEligible(eligibleSubtotal, tier) {
    return tier.thresholdCents > 0 && eligibleSubtotal >= tier.thresholdCents;
  }

  getTier(tierId) {
    return this.tiers.find((tier) => tier.id === tierId);
  }

  isTier2Blocked(eligibleSubtotal, dismissState) {
    const tier2 = this.getTier(2);
    if (!tier2) return false;
    return this.isTierEligible(eligibleSubtotal, tier2) && dismissState[2];
  }

  async syncTierDismissState(cart, tier, fingerprint, fingerprintChanged, dismissState) {
    const giftLine = this.getGiftLine(cart, tier.id);
    const eligibleSubtotal = this.getEligibleSubtotal(cart);
    const isEligible = this.isTierEligible(eligibleSubtotal, tier);
    const prev = this.previousCart;
    const prevGiftLine = prev ? this.getGiftLine(prev, tier.id) : null;
    const prevEligible = prev ? this.isTierEligible(this.getEligibleSubtotal(prev), tier) : false;
    let dismissed = this.isDismissed(cart, tier.id);
    let attributesChanged = false;

    if (
      prevGiftLine &&
      !giftLine &&
      prevEligible &&
      isEligible &&
      this.previousFingerprint === fingerprint
    ) {
      const tier2 = this.getTier(2);
      const upgradedToTier2 =
        tier.id === 1 &&
        tier2 &&
        this.isTierEligible(eligibleSubtotal, tier2) &&
        Boolean(this.getGiftLine(cart, 2));

      if (!upgradedToTier2) {
        await this.setDismissed(tier.id, true);
        dismissed = true;
        attributesChanged = true;
        dismissState[tier.id] = true;
      }
    }

    if ((!isEligible || fingerprintChanged) && dismissed) {
      await this.setDismissed(tier.id, false);
      dismissed = false;
      attributesChanged = true;
      dismissState[tier.id] = false;
    }

    return attributesChanged;
  }

  async applyGiftState(cart, dismissState) {
    const tier1 = this.getTier(1);
    const tier2 = this.getTier(2);
    const eligibleSubtotal = this.getEligibleSubtotal(cart);
    const tier1Line = tier1 ? this.getGiftLine(cart, 1) : null;
    const tier2Line = tier2 ? this.getGiftLine(cart, 2) : null;

    const tier2Eligible = tier2 && this.isTierEligible(eligibleSubtotal, tier2);
    const tier1Eligible = tier1 && this.isTierEligible(eligibleSubtotal, tier1);
    const tier2Active =
      tier2Eligible && !dismissState[2] && tier2.variantAvailable;
    const tier2Blocked = this.isTier2Blocked(eligibleSubtotal, dismissState);
    const tier1Active =
      tier1Eligible &&
      !dismissState[1] &&
      tier1.variantAvailable &&
      !tier2Active &&
      !tier2Blocked;

    if (tier2Active) {
      if (tier1Line && !tier2Line) {
        return { mutated: true, cart: await this.replaceGift(cart, tier1Line, tier2) };
      }
      if (tier1Line && tier2Line) {
        return { mutated: true, cart: await this.removeGift(cart, tier1Line) };
      }
      if (!tier2Line) {
        return {
          mutated: true,
          cart: await this.resolveCartResponse(await this.addGift(tier2)),
        };
      }
      return { mutated: false, cart };
    }

    if (tier1Active) {
      if (tier2Line && !tier1Line) {
        return { mutated: true, cart: await this.replaceGift(cart, tier2Line, tier1) };
      }
      if (tier2Line && tier1Line) {
        return { mutated: true, cart: await this.removeGift(cart, tier2Line) };
      }
      if (!tier1Line) {
        return {
          mutated: true,
          cart: await this.resolveCartResponse(await this.addGift(tier1)),
        };
      }
      return { mutated: false, cart };
    }

    let updatedCart = cart;
    let mutated = false;

    if (tier1Line) {
      updatedCart = await this.removeGift(updatedCart, tier1Line);
      mutated = true;
    }

    const remainingTier2Line = mutated ? this.getGiftLine(updatedCart, 2) : tier2Line;
    if (remainingTier2Line) {
      updatedCart = await this.removeGift(updatedCart, remainingTier2Line);
      mutated = true;
    }

    return { mutated, cart: updatedCart };
  }

  async sync() {
    if (this.syncing) return;
    this.syncing = true;

    try {
      let cart = await this.fetchCart();
      const fingerprint = this.getNonGiftFingerprint(cart);
      const fingerprintChanged =
        this.previousFingerprint !== null && this.previousFingerprint !== fingerprint;
      let attributesChanged = false;
      const dismissState = {};

      for (const tier of this.tiers) {
        dismissState[tier.id] = this.isDismissed(cart, tier.id);
      }

      for (const tier of this.tiers) {
        if (
          await this.syncTierDismissState(
            cart,
            tier,
            fingerprint,
            fingerprintChanged,
            dismissState
          )
        ) {
          attributesChanged = true;
        }
      }

      if (attributesChanged) {
        cart = await this.fetchCart();
        for (const tier of this.tiers) {
          dismissState[tier.id] = this.isDismissed(cart, tier.id);
        }
      }

      const result = await this.applyGiftState(cart, dismissState);
      const mutated = result.mutated;

      if (mutated) {
        cart = result.cart ?? (await this.fetchCart());
        await this.refreshCartUI();
        await this.refreshCartIcon();
        this.previousCart = cart;
        this.previousFingerprint = this.getNonGiftFingerprint(cart);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: cart });
        return;
      }

      this.previousCart = cart;
      this.previousFingerprint = fingerprint;
    } catch (error) {
      console.error('Free gift sync failed:', error);
    } finally {
      this.syncing = false;
    }
  }
}

new FreeGiftManager();
